import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  TextInput,
  NumberInput,
  MultiSelect,
  Switch,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Divider,
  Text,
  Box,
  Center,
  Loader,
  useComputedColorScheme,
} from "@mantine/core";
import { CircleF, GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import type { TollLocation, TollVehicleType } from "../../types/domain";
import { GOOGLE_MAPS_API_KEY } from "../../config/env";
import { tokensFor } from "../../theme/tokens";

export interface TollLocationFormValues {
  name: { en: string; sw: string };
  latitude: number;
  longitude: number;
  radiusMeters: number;
  feeTZS: number;
  appliesToVehicleTypes: TollVehicleType[];
  isActive: boolean;
}

const VEHICLE_OPTIONS: { value: TollVehicleType; label: string }[] = [
  { value: "car", label: "Car (taxi)" },
  { value: "motorcycle", label: "Motorcycle (boda)" },
  { value: "tricycle", label: "Tricycle (bajaj)" },
];

// Default centre = central Dar es Salaam, so the preview map isn't blank.
const EMPTY: TollLocationFormValues = {
  name: { en: "", sw: "" },
  latitude: -6.7924,
  longitude: 39.2083,
  radiusMeters: 150,
  feeTZS: 2000,
  appliesToVehicleTypes: ["car", "tricycle"],
  isActive: true,
};

function fromTollLocation(t: TollLocation): TollLocationFormValues {
  const [lng, lat] = t.center?.coordinates ?? [
    EMPTY.longitude,
    EMPTY.latitude,
  ];
  return {
    name: { en: t.name?.en ?? "", sw: t.name?.sw ?? "" },
    latitude: lat,
    longitude: lng,
    radiusMeters: t.radiusMeters ?? EMPTY.radiusMeters,
    feeTZS: t.feeTZS ?? EMPTY.feeTZS,
    appliesToVehicleTypes:
      t.appliesToVehicleTypes ?? EMPTY.appliesToVehicleTypes,
    isActive: t.isActive ?? true,
  };
}

/**
 * Google map for placing the toll. Click the map or drag the pin to set the
 * centre (the numeric inputs stay in sync); the circle is the charging radius.
 */
function TollMapPicker({
  lat,
  lon,
  radiusMeters,
  onPick,
}: {
  lat: number;
  lon: number;
  radiusMeters: number;
  onPick: (lat: number, lon: number) => void;
}) {
  const scheme = useComputedColorScheme("light");
  // Google Maps can't read CSS variables — resolve the tokens for this theme.
  const tk = useMemo(() => tokensFor(scheme), [scheme]);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "safiri-admin-maps",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const valid = Number.isFinite(lat) && Number.isFinite(lon);
  const center = useMemo(() => ({ lat, lng: lon }), [lat, lon]);

  // Keep the whole radius in view as the centre or radius changes.
  useEffect(() => {
    if (!map || !valid) return;
    const bounds = new google.maps.Circle({
      center,
      radius: Math.max(radiusMeters * 3, 200),
    }).getBounds();
    if (bounds) map.fitBounds(bounds, 0);
  }, [map, valid, center, radiusMeters]);

  const pick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) onPick(e.latLng.lat(), e.latLng.lng());
  };

  if (!valid) return null;
  return (
    <Box>
      <Text size="xs" c="dimmed" mb={4}>
        Click the map or drag the pin to set the toll centre
      </Text>
      {!GOOGLE_MAPS_API_KEY || loadError ? (
        <Text size="sm" c="dimmed">
          Map unavailable. Set the coordinates with the fields above.
        </Text>
      ) : !isLoaded ? (
        <Center h={280}>
          <Loader />
        </Center>
      ) : (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: 280, borderRadius: 8 }}
          center={center}
          zoom={16}
          onLoad={setMap}
          onUnmount={() => setMap(null)}
          onClick={pick}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: true,
            gestureHandling: "greedy",
          }}
        >
          <CircleF
            center={center}
            radius={radiusMeters}
            options={{
              strokeColor: tk["warning-solid"],
              strokeWeight: 2,
              fillColor: tk["warning-solid"],
              fillOpacity: 0.2,
              clickable: false,
            }}
          />
          <MarkerF position={center} draggable onDragEnd={pick} />
        </GoogleMap>
      )}
      <Text size="xs" c="dimmed" mt={4}>
        Charged when a trip's route passes within {radiusMeters} m of this point.
      </Text>
    </Box>
  );
}

export function TollLocationFormModal({
  opened,
  onClose,
  editing,
  loading,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  editing: TollLocation | null;
  loading: boolean;
  onSubmit: (values: TollLocationFormValues) => void;
}) {
  const [v, setV] = useState<TollLocationFormValues>(EMPTY);

  useEffect(() => {
    if (opened) setV(editing ? fromTollLocation(editing) : EMPTY);
  }, [opened, editing]);

  const valid =
    v.name.en.trim() &&
    v.name.sw.trim() &&
    v.appliesToVehicleTypes.length > 0 &&
    Number.isFinite(v.latitude) &&
    Number.isFinite(v.longitude) &&
    v.radiusMeters > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? "Edit toll location" : "New toll location"}
      size="lg"
      centered
    >
      <Stack>
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          Name (bilingual)
        </Text>
        <SimpleGrid cols={2}>
          <TextInput
            label="Name (English)"
            placeholder="Nyerere Bridge"
            value={v.name.en}
            onChange={(e) =>
              setV({ ...v, name: { ...v.name, en: e.currentTarget.value } })
            }
            required
          />
          <TextInput
            label="Name (Swahili)"
            placeholder="Daraja la Nyerere"
            value={v.name.sw}
            onChange={(e) =>
              setV({ ...v, name: { ...v.name, sw: e.currentTarget.value } })
            }
            required
          />
        </SimpleGrid>

        <Divider label="Location & radius" labelPosition="left" />
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <NumberInput
            label="Latitude"
            value={v.latitude}
            onChange={(val) => setV({ ...v, latitude: Number(val) })}
            decimalScale={6}
            step={0.0001}
            min={-90}
            max={90}
          />
          <NumberInput
            label="Longitude"
            value={v.longitude}
            onChange={(val) => setV({ ...v, longitude: Number(val) })}
            decimalScale={6}
            step={0.0001}
            min={-180}
            max={180}
          />
          <NumberInput
            label="Radius (metres)"
            value={v.radiusMeters}
            onChange={(val) => setV({ ...v, radiusMeters: Number(val) || 0 })}
            min={1}
            max={20000}
          />
        </SimpleGrid>

        <TollMapPicker
          lat={v.latitude}
          lon={v.longitude}
          radiusMeters={v.radiusMeters}
          onPick={(latitude, longitude) =>
            setV((prev) => ({
              ...prev,
              latitude: Number(latitude.toFixed(6)),
              longitude: Number(longitude.toFixed(6)),
            }))
          }
        />

        <Divider label="Fee & applicability" labelPosition="left" />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <NumberInput
            label="Fee (TZS)"
            value={v.feeTZS}
            onChange={(val) => setV({ ...v, feeTZS: Number(val) || 0 })}
            min={0}
            thousandSeparator=","
          />
          <MultiSelect
            label="Applies to vehicle types"
            data={VEHICLE_OPTIONS}
            value={v.appliesToVehicleTypes}
            onChange={(val) =>
              setV({ ...v, appliesToVehicleTypes: val as TollVehicleType[] })
            }
            required
          />
        </SimpleGrid>

        <Switch
          label="Active"
          checked={v.isActive}
          onChange={(e) => setV({ ...v, isActive: e.currentTarget.checked })}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!valid}
            onClick={() => onSubmit(v)}
          >
            {editing ? "Save changes" : "Create"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
